import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  DependencyViewerApi,
  StreamCallbacks,
} from './DependencyViewerApi';
import { DependencyResponse } from '../utils/types';

interface CacheEntry {
  data: DependencyResponse;
  expiresAt: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

export class DependencyViewerClient implements DependencyViewerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly cache = new Map<string, CacheEntry>();

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private getCacheKey(projectSlug: string, manifestPaths?: string[]): string {
    const paths = manifestPaths?.slice().sort().join(',') ?? '';
    return `${projectSlug}::${paths}`;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('dependency-viewer');
  }

  async getDependencies(projectSlug: string, manifestPaths?: string[]): Promise<DependencyResponse> {
    const key = this.getCacheKey(projectSlug, manifestPaths);
    const cached = this.cache.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.data;
    }

    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({ slug: projectSlug });
    if (manifestPaths && manifestPaths.length > 0) {
      params.set('paths', manifestPaths.join(','));
    }
    const res = await this.fetchApi.fetch(`${baseUrl}/dependencies?${params}`);
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as any;
      throw new Error(
        body?.error?.message ?? `Request failed with status ${res.status}`,
      );
    }
    const data = await res.json() as DependencyResponse;
    this.cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
    return data;
  }

  /**
   * Opens a streaming connection using fetch + ReadableStream to consume SSE.
   * This approach works with Backstage's fetchApi which handles auth headers,
   * unlike native EventSource which doesn't support custom headers.
   */
  streamDependencies(
    projectSlug: string,
    manifestPaths: string[] | undefined,
    callbacks: StreamCallbacks,
  ): () => void {
    // Check cache first
    const key = this.getCacheKey(projectSlug, manifestPaths);
    const cached = this.cache.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      callbacks.onManifests({
        manifests: cached.data.manifests,
        dependencies: cached.data.dependencies,
        totalCount: cached.data.dependencies.length,
      });
      callbacks.onDone({ summary: cached.data.summary });
      return () => {};
    }

    const controller = new AbortController();

    const run = async () => {
      try {
        const baseUrl = await this.getBaseUrl();
        const params = new URLSearchParams({ slug: projectSlug });
        if (manifestPaths && manifestPaths.length > 0) {
          params.set('paths', manifestPaths.join(','));
        }

        const res = await this.fetchApi.fetch(
          `${baseUrl}/dependencies/stream?${params}`,
          { signal: controller.signal },
        );

        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as any;
          throw new Error(
            body?.error?.message ?? `Stream request failed with status ${res.status}`,
          );
        }

        const reader = res.body?.getReader();
        if (!reader) {
          throw new Error('ReadableStream not supported');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const events = parseSSEBuffer(buffer);
          buffer = events.remaining;

          for (const event of events.parsed) {
            if (controller.signal.aborted) return;

            try {
              const data = JSON.parse(event.data);
              switch (event.type) {
                case 'manifests':
                  callbacks.onManifests(data);
                  break;
                case 'batch':
                  callbacks.onBatch(data);
                  break;
                case 'done':
                  callbacks.onDone(data);
                  break;
                case 'error':
                  callbacks.onError(new Error(data.message ?? 'Stream error'));
                  break;
              }
            } catch {
              // Skip malformed events
            }
          }
        }
      } catch (err: any) {
        if (controller.signal.aborted) return;
        callbacks.onError(err instanceof Error ? err : new Error(String(err)));
      }
    };

    run();

    return () => {
      controller.abort();
    };
  }

  invalidateCache(projectSlug: string, manifestPaths?: string[]): void {
    const key = this.getCacheKey(projectSlug, manifestPaths);
    this.cache.delete(key);
  }

  /** Store a complete response in cache (called by the hook after streaming completes) */
  cacheResponse(projectSlug: string, manifestPaths: string[] | undefined, data: DependencyResponse): void {
    const key = this.getCacheKey(projectSlug, manifestPaths);
    this.cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
  }
}

interface ParsedSSEEvent {
  type: string;
  data: string;
}

interface ParseResult {
  parsed: ParsedSSEEvent[];
  remaining: string;
}

/**
 * Parses SSE events from a text buffer.
 * SSE format: "event: <type>\ndata: <json>\n\n"
 */
function parseSSEBuffer(buffer: string): ParseResult {
  const parsed: ParsedSSEEvent[] = [];
  const blocks = buffer.split('\n\n');

  // The last block might be incomplete
  const remaining = blocks.pop() ?? '';

  for (const block of blocks) {
    if (!block.trim()) continue;

    let eventType = 'message';
    let data = '';

    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        data = line.slice(6);
      }
    }

    if (data) {
      parsed.push({ type: eventType, data });
    }
  }

  return { parsed, remaining };
}
